import { useState } from "react";
import { toast } from "sonner";
import { AlertCircle, Paperclip, X, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { createReport } from "@/lib/api";

const MAX_ATTACHMENT_MB = 10;

export default function ReportForm({ onSubmitted }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [contact, setContact] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) {
      setFile(null);
      return;
    }
    if (f.size > MAX_ATTACHMENT_MB * 1024 * 1024) {
      toast.error(`Attachment too large (max ${MAX_ATTACHMENT_MB} MB)`);
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error("Please add a short title");
      return;
    }
    setSubmitting(true);
    try {
      await createReport({ title, description, contact, file });
      toast.success("Report submitted. Thank you!");
      setTitle("");
      setDescription("");
      setContact("");
      setFile(null);
      // Reset the file input
      const fi = document.querySelector('input[data-testid="report-file-input"]');
      if (fi) fi.value = "";
      onSubmitted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit report");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="report-form"
      className="bg-[#F8FAFC] border border-slate-200 p-5 space-y-4"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 bg-[#2563EB] flex items-center justify-center shrink-0">
          <AlertCircle className="w-5 h-5 text-black" strokeWidth={2.5} />
        </div>
        <div>
          <div className="overline">New report</div>
          <div className="heading text-lg font-semibold">Report a Problem</div>
          <div className="mono text-[11px] text-[#64748B] mt-1">
            Wrong load plan? Buggy container split? Extraction missed cases? Tell us.
          </div>
        </div>
      </div>

      <div>
        <label htmlFor="report-title" className="overline block mb-1">
          Title <span className="text-[#EF4444]">*</span>
        </label>
        <Input
          id="report-title"
          data-testid="report-title-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Under-utilised 20ft container for Sabarmati load"
          maxLength={200}
          className="bg-[#FFFFFF] border-slate-200 rounded-none focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] mono text-sm"
          required
        />
      </div>

      <div>
        <label htmlFor="report-description" className="overline block mb-1">Description</label>
        <Textarea
          id="report-description"
          data-testid="report-description-input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Steps to reproduce, expected vs actual, or notes for us."
          rows={4}
          maxLength={5000}
          className="bg-[#FFFFFF] border-slate-200 rounded-none focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] mono text-xs"
        />
      </div>

      <div>
        <label htmlFor="report-contact" className="overline block mb-1">Your contact (optional)</label>
        <Input
          id="report-contact"
          data-testid="report-contact-input"
          value={contact}
          onChange={(e) => setContact(e.target.value)}
          placeholder="email / phone / name — so we can follow up"
          maxLength={120}
          className="bg-[#FFFFFF] border-slate-200 rounded-none focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] mono text-sm"
        />
      </div>

      <div>
        <label className="overline block mb-1">Attachment (optional, ≤ 10 MB)</label>
        <div className="flex items-center gap-2">
          <label
            htmlFor="report-file-input"
            className="cursor-pointer bg-[#FFFFFF] border border-slate-200 hover:border-[#2563EB] px-4 py-2 mono text-xs text-slate-700 hover:text-[#2563EB] transition-colors duration-200 flex items-center gap-2"
          >
            <Paperclip className="w-3.5 h-3.5" />
            {file ? "Change file" : "Choose file"}
          </label>
          <input
            type="file"
            id="report-file-input"
            data-testid="report-file-input"
            onChange={handleFile}
            accept="image/*,application/pdf,.xls,.xlsx"
            className="hidden"
          />
          {file && (
            <div className="flex items-center gap-2 mono text-xs text-[#64748B]">
              <span className="text-white/90">{file.name}</span>
              <span>({(file.size / 1024).toFixed(0)} KB)</span>
              <button
                type="button"
                onClick={() => {
                  setFile(null);
                  const fi = document.querySelector('input[data-testid="report-file-input"]');
                  if (fi) fi.value = "";
                }}
                className="text-slate-500 hover:text-[#EF4444]"
                aria-label="Remove attachment"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="pt-2 flex justify-end">
        <Button
          type="submit"
          data-testid="submit-report-btn"
          disabled={submitting || !title.trim()}
          className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-md font-semibold mono uppercase tracking-widest text-xs px-6"
        >
          <Send className="w-3.5 h-3.5 mr-2" />
          {submitting ? "Submitting…" : "Submit Report"}
        </Button>
      </div>
    </form>
  );
}
